/**
 * Remediation C3 — Firestore-safe notification/partnerRequest queries.
 * Rules require party-field scoping; poNumber-only list queries are rejected.
 */
import {
  collection,
  deleteDoc,
  type DocumentData,
  type Firestore,
  getDocs,
  query,
  type QueryConstraint,
  type QueryDocumentSnapshot,
  type UpdateData,
  where,
  writeBatch,
} from 'firebase/firestore';
import {
  FIRESTORE_NOTIFICATIONS_COLLECTION,
  FIRESTORE_PARTNER_REQUESTS_COLLECTION,
} from '../constants/firestoreCollectionSecurity';

const NOTIFICATION_PARTY_FIELDS = [
  'recipientId',
  'senderId',
  'shipperId',
  'brokerId',
  'carrierId',
  'userId',
] as const;

const PARTNER_REQUEST_PARTY_FIELDS = ['carrierId', 'shipperId', 'brokerId'] as const;

async function mergePartyFieldQueries(
  db: Firestore,
  collectionName: string,
  partyUid: string,
  partyFields: readonly string[],
  extraConstraints: QueryConstraint[] = []
): Promise<QueryDocumentSnapshot<DocumentData>[]> {
  const byId = new Map<string, QueryDocumentSnapshot<DocumentData>>();
  await Promise.all(
    partyFields.map(async (field) => {
      const snap = await getDocs(
        query(
          collection(db, collectionName),
          where(field, '==', partyUid),
          ...extraConstraints
        )
      );
      snap.docs.forEach((d) => byId.set(d.id, d));
    })
  );
  return [...byId.values()];
}

/** Notifications for a PO where the user is a party (recipient, sender, or role field). */
export async function getNotificationDocsForPoAndParty(
  db: Firestore,
  poNumber: string,
  partyUid: string
): Promise<QueryDocumentSnapshot<DocumentData>[]> {
  return mergePartyFieldQueries(db, FIRESTORE_NOTIFICATIONS_COLLECTION, partyUid, NOTIFICATION_PARTY_FIELDS, [
    where('poNumber', '==', poNumber),
  ]);
}

/** Partner requests for a PO where the user is a party. */
export async function getPartnerRequestDocsForPoAndParty(
  db: Firestore,
  poNumber: string,
  partyUid: string,
  extraConstraints: QueryConstraint[] = []
): Promise<QueryDocumentSnapshot<DocumentData>[]> {
  return mergePartyFieldQueries(
    db,
    FIRESTORE_PARTNER_REQUESTS_COLLECTION,
    partyUid,
    PARTNER_REQUEST_PARTY_FIELDS,
    [where('poNumber', '==', poNumber), ...extraConstraints]
  );
}

/** Delete all notifications for a PO visible to the party user. */
export async function deleteNotificationsForPoAndParty(
  db: Firestore,
  poNumber: string,
  partyUid: string
): Promise<number> {
  const docs = await getNotificationDocsForPoAndParty(db, poNumber, partyUid);
  await Promise.all(docs.map((d) => deleteDoc(d.ref)));
  return docs.length;
}

/** Delete all partner requests for a PO visible to the party user. */
export async function deletePartnerRequestsForPoAndParty(
  db: Firestore,
  poNumber: string,
  partyUid: string,
  extraConstraints: QueryConstraint[] = []
): Promise<number> {
  const docs = await getPartnerRequestDocsForPoAndParty(db, poNumber, partyUid, extraConstraints);
  await Promise.all(docs.map((d) => deleteDoc(d.ref)));
  return docs.length;
}

/** Batch-update notifications for a PO visible to the party user. */
export async function updateNotificationsForPoAndParty(
  db: Firestore,
  poNumber: string,
  partyUid: string,
  payload: Record<string, unknown>
): Promise<number> {
  const docs = await getNotificationDocsForPoAndParty(db, poNumber, partyUid);
  if (docs.length === 0) return 0;
  const batch = writeBatch(db);
  docs.forEach((d) => batch.update(d.ref, payload as UpdateData<DocumentData>));
  await batch.commit();
  return docs.length;
}

/** Filter party-scoped notification docs by type/status in memory (rules-safe queries). */
export function filterNotificationDocs(
  docs: QueryDocumentSnapshot<DocumentData>[],
  filter: { types?: readonly string[]; status?: string }
): QueryDocumentSnapshot<DocumentData>[] {
  return docs.filter((d) => {
    const data = d.data();
    if (filter.types && !filter.types.includes(data.type)) return false;
    if (filter.status !== undefined && data.status !== filter.status) return false;
    return true;
  });
}
